/**
 * Select Widget
 * This was designed to replace `react-select` in cases where it was too difficult to override. Please
 * consider this experimental for right now and use at your own risk.
 *
 * Usage:
 * const options = [{ label: "Value 1", value: "1" }]
 * const selectedValue = options[0]
 * <Select value={selectedValue} options={options} onChange={(option) => { doSomething(option) }} />
 *
 * More Examples:
 * See `Select.stories.tsx`
 */

import { CaretDownOutlined } from '@ant-design/icons';
import { Listbox } from '@headlessui/react';
import classNames from 'classnames';

export type SelectOption = {
  value: string | number | undefined;
  label: string;
};

// This is kind of a weird feature that allows the UI to nest grouped options
// and is activated if you have a `|` in your `value`, i.e.
//
// const options = [
//   { value: "grouped-option", label: "Grouped Options" },
//   { value: "grouped-option|1", label: "Option 1" },
// ]
// Note: This is here to support a weird edge case in
// the <BorrowerSharingSelect /> component, but I am SURE
// there is a better way to do this
const isNested = (value: string) => /\|/.test(value);

const Option = <T extends SelectOption>({ option }: { option: T }) => {
  const isNestedOption = typeof option.value === 'string' ? isNested(option.value) : false;

  return (
    <Listbox.Option value={option}>
      {({ active, selected }) => (
        <div
          className={classNames({
            'u-bg-pine1': active,
          })}
        >
          <div
            className={classNames('d-flex gap-2 align-items-center py-1 u-pointer', {
              'px-2': !isNestedOption,
              'px-4': isNestedOption,
            })}
          >
            {/*
              This is used to apply different UI treatments to items belonging to a group. In this
              case we are assuming that multiple children of a parent option can be selected so we
              display a checkbox as opposed to radio input that makes this more obvious. This should
              hopefully make it clear to end users they can select multiple children, but only one
              parent. We will likely revisit this in the future to make it more flexible.
             */}
            {isNestedOption ? (
              <input type="checkbox" className="form-check-input" checked={selected} readOnly />
            ) : undefined}
            {option.label}
          </div>
        </div>
      )}
    </Listbox.Option>
  );
};

// T extends SelectOption just ensures that each option in the `options` prop (and also `value`)
// is an object that looks like `{ label: string; value: string; }`. It can also have other arbitrary
// keys.
//
// Using a type instead of interface here so we can change the `value` and `onChange` props to
// expect an array of options or a single option depending on if multiple selections are allowed
type SelectProps<T extends SelectOption> =
  | {
      multiple: true;
      value: T[];
      displayValue?: string;
      options: T[];
      onChange: (selectedOption: T[]) => void;
      disabled?: boolean;
    }
  | {
      multiple?: false;
      value: T;
      displayValue?: string;
      options: T[];
      onChange: (selectedOptions: T) => void;
      disabled?: boolean;
    };

export const Select = <T extends SelectOption>({
  displayValue,
  options,
  value,
  multiple,
  onChange,
  disabled,
}: SelectProps<T>) => {
  return (
    <Listbox value={value} by="value" multiple={multiple} onChange={onChange} disabled={disabled}>
      <Listbox.Button className="u-unset w-100 ">
        <div
          className={classNames(
            'd-flex p-2 align-items-center justify-content-between u-border-1 u-border-r-3 u-border-color-bark1',
            {
              'u-bg-white hover:u-border-color-bark2': !disabled,
              'u-bg-bark1': disabled,
            },
          )}
        >
          <div className="pe-2">
            {displayValue ??
              (Array.isArray(value) ? value : [value])
                .filter((op) => (typeof op.value === 'string' ? !isNested(op.value) : true))
                .map((op) => op.label)
                .join(', ')}
          </div>
          <CaretDownOutlined />
        </div>
      </Listbox.Button>

      <Listbox.Options className="list-unstyled position-relative u-z-100">
        <div className="position-absolute">
          <div className="u-bg-white">
            <div
              className="mt-1 py-1 u-border-1 u-border-color-bark1"
              style={{ maxHeight: 150, overflowY: 'auto', overflowX: 'auto' }}
            >
              {options.map((option) => (
                <Option key={option.value ?? 'undefined'} option={option} />
              ))}
            </div>
          </div>
        </div>
      </Listbox.Options>
    </Listbox>
  );
};
